import React from 'react';
import { Box, Button, Chip, Grid, Link, Paper, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import InboxIcon from '@mui/icons-material/Inbox';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { FilterInput, FilterMultiSelect, FilterSelect } from '../../../components/filter-fields';
import { DateField } from '../../../components/form-page-parts';
import { PagedGrid } from '../../../components/paged-grid';
import { RowAction } from '../../../components/row-action';
import { TwoLineCell } from '../../../components/two-line-cell';
import { EmptyState } from '../../../components/empty-state';
import DefaultSelectClient from '../../../components/default-select-client';
import { usePagedList } from '../../../hooks/use-paged-list';
import SubmissionAppService from '../../../application/platform/ser/submission/submission-app-service';
import { SUBMISSION_STATUS_LABEL, SUBMISSION_TYPE_LABEL, SubmissionStatus, SubmissionType } from '../../../model/ser/ser-enums';
import { ISubmissionItemResponse } from '../../../model/ser/submission/response/submission-item-response';
import { formatDate, formatDateTime } from '../../../utils/date';
import { lookupOptions } from '../../../utils/filter-options';
import { SubmissionStatusChip, SubmissionTypeChip } from '../components/submission-chips';
import { CancelSubmissionDialog } from '../components/cancel-submission-dialog';
import { useSerClient } from '../components/use-ser-client';
import { useSubmissionSearchState } from './context/submission-search-context';
import { useSubmissionSearchController } from './context/submission-search-controller';
import { CANNOT_CANCEL_HINT, SubmissionDetailDrawer } from './components/submission-detail-drawer';
import { BatchDetailDrawer } from './components/batch-detail-drawer';

const TYPE_OPTIONS = [SubmissionType.Rotina, SubmissionType.Urgencia].map((type) => ({ value: type, label: SUBMISSION_TYPE_LABEL[type] }));

function stop(e: React.SyntheticEvent) {
    e.stopPropagation();
}

function VehiclesCell({ vehicles }: { vehicles?: string[] }) {
    if (!vehicles?.length) return null;
    const [first, ...rest] = vehicles;
    return (
        <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
            <Chip size="small" label={first} sx={{ maxWidth: 140 }} />
            {rest.length > 0 && (
                <Tooltip title={rest.join(', ')}>
                    <Chip size="small" variant="outlined" label={`+${rest.length}`} />
                </Tooltip>
            )}
        </Box>
    );
}

function SubmissionGrid({ ctrl, isClientUser }: { ctrl: ReturnType<typeof useSubmissionSearchController>; isClientUser: boolean }) {
    const navigate = useNavigate();
    const { parameters, searchTrigger } = useSubmissionSearchState();
    const list = usePagedList(SubmissionAppService.list, parameters, searchTrigger, 'Não foi possível carregar as remessas');

    const columns: GridColDef<ISubmissionItemResponse>[] = [
        { field: 'number', headerName: 'Nº', width: 80, sortable: false, valueGetter: (params) => params.row.id },
        {
            field: 'title', headerName: 'Título', flex: 1, minWidth: 240, sortable: false,
            renderCell: (params) => (
                <TwoLineCell
                    bold
                    primary={params.row.title}
                    secondary={[params.row.userSenderName, isClientUser ? undefined : params.row.clientName].filter(Boolean).join(' · ')}
                />
            ),
        },
        {
            field: 'statusId', headerName: 'Status', width: 150, sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SubmissionStatusChip statusId={params.row.statusId} label={params.row.status} />
                    <SubmissionTypeChip type={params.row.type} compact />
                </Box>
            ),
        },
        { field: 'sendDate', headerName: 'Envio', width: 140, sortable: false, valueFormatter: (params) => formatDateTime(params.value) },
        { field: 'publishDate', headerName: 'Publicação', width: 110, sortable: false, valueFormatter: (params) => formatDate(params.value) },
        { field: 'vehicles', headerName: 'Veículos', width: 200, sortable: false, renderCell: (params) => <VehiclesCell vehicles={params.row.vehicles} /> },
        { field: 'documentCount', headerName: 'Docs', width: 70, type: 'number', sortable: false },
        {
            field: 'batchId', headerName: 'Lote', width: 90, sortable: false,
            renderCell: (params) => params.row.batchId
                ? <Link component="button" variant="body2" onClick={(e: React.MouseEvent) => { stop(e); ctrl.openBatch(params.row.batchId!); }}>nº {params.row.batchId}</Link>
                : null,
        },
        {
            field: 'actions', headerName: 'Ações', width: 100, sortable: false,
            renderCell: (params) => (
                <Box onClick={stop} sx={{ display: 'flex' }}>
                    <RowAction title="Ver detalhes" onClick={() => ctrl.openDetail(params.row.id)}><VisibilityIcon fontSize="small" /></RowAction>
                    <RowAction
                        title={params.row.canCancel ? 'Cancelar remessa' : CANNOT_CANCEL_HINT}
                        color="error"
                        disabled={!params.row.canCancel}
                        onClick={() => ctrl.openCancel({ id: params.row.id, title: params.row.title })}
                    >
                        <BlockIcon fontSize="small" />
                    </RowAction>
                </Box>
            ),
        },
    ];

    if (!list.loading && list.rowCount === 0) {
        return (
            <Paper sx={{ p: 2 }}>
                <EmptyState icon={<InboxIcon />} message="Nenhuma remessa encontrada." actionLabel="Enviar remessa" onAction={() => navigate('/enviar-remessa')} />
            </Paper>
        );
    }

    return (
        <PagedGrid
            columns={columns}
            list={list}
            parameters={parameters}
            onPaginationChange={ctrl.handlePaginationChange}
            addLabel="Enviar remessa"
            onAdd={() => navigate('/enviar-remessa')}
            rowHeight={60}
            onRowClick={(row: ISubmissionItemResponse) => ctrl.openDetail(row.id)}
        />
    );
}

export function SubmissionSearchComponent() {
    const navigate = useNavigate();
    const client = useSerClient();
    const ctrl = useSubmissionSearchController(client.clientId, client.isClientUser);
    const state = useSubmissionSearchState();
    const { parameters, statuses, vehicles, periodErrors } = state;

    const statusOptions = statuses.length
        ? lookupOptions(statuses)
        : Object.entries(SUBMISSION_STATUS_LABEL).map(([id, label]) => ({ value: Number(id) as SubmissionStatus, label }));

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Remessas"
                    subtitle="Acompanhe as remessas enviadas, baixe os arquivos e cancele envios feitos por engano"
                    breadcrumbs={[{ label: 'Remessas' }]}
                    actions={<Button variant="contained" startIcon={<SendIcon />} onClick={() => navigate('/enviar-remessa')}>Enviar remessa</Button>}
                />
            </Grid>
            {state.ready && (
                <React.Fragment>
                    <Grid item xs={12}>
                        <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch} defaultOpen={state.filtersOpen}>
                            <Grid item xs={12} md={4}>
                                <FilterInput label="Título" name="input-title" value={parameters.title} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <FilterInput label="Conteúdo dos documentos" name="input-content" value={parameters.content} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FilterInput label="Nº do lote" name="input-batch" type="number" value={parameters.batchId} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FilterMultiSelect label="Status" name="select-status" value={parameters.statusIds ?? []} options={statusOptions} onChange={ctrl.handleOnChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FilterMultiSelect label="Tipo" name="select-type" value={parameters.types ?? []} options={TYPE_OPTIONS} onChange={ctrl.handleOnChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FilterSelect
                                    label="Veículo"
                                    name="select-vehicle"
                                    value={parameters.vehicleId}
                                    options={vehicles.map((v) => ({ value: v.id, label: v.name || '' }))}
                                    disabled={vehicles.length === 0}
                                    onChange={ctrl.handleOnChange}
                                />
                            </Grid>
                            {!client.isClientUser && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <DefaultSelectClient required={false} clientId={parameters.clientId} handleOnChange={ctrl.handleOnChange} />
                                </Grid>
                            )}
                            <Grid item xs={6} md={3}>
                                <DateField label="Enviada de" value={parameters.startDate} onChange={(v) => ctrl.handleDateChange('startDate', v)} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DateField label="Enviada até" value={parameters.endDate} error={periodErrors.send} onChange={(v) => ctrl.handleDateChange('endDate', v)} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DateField label="Publicação de" value={parameters.publishStartDate} onChange={(v) => ctrl.handleDateChange('publishStartDate', v)} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DateField label="Publicação até" value={parameters.publishEndDate} error={periodErrors.publish} onChange={(v) => ctrl.handleDateChange('publishEndDate', v)} />
                            </Grid>
                        </FilterPanel>
                    </Grid>
                    <Grid item xs={12}>
                        <SubmissionGrid ctrl={ctrl} isClientUser={client.isClientUser} />
                    </Grid>
                </React.Fragment>
            )}

            <SubmissionDetailDrawer
                submissionId={state.detailId}
                refreshVersion={state.refreshVersion}
                onClose={ctrl.closeDetail}
                onOpenBatch={ctrl.openBatch}
                onCancel={ctrl.openCancel}
                onChanged={ctrl.refresh}
            />
            <BatchDetailDrawer
                batchId={state.batchId}
                refreshVersion={state.refreshVersion}
                onClose={ctrl.closeBatch}
                onOpenSubmission={ctrl.openDetail}
                onFilterByBatch={ctrl.filterByBatch}
                onChanged={ctrl.refresh}
            />
            <CancelSubmissionDialog
                open={!!state.cancelTarget}
                title="Cancelar remessa"
                summary={<strong>Remessa nº {state.cancelTarget?.id}{state.cancelTarget?.title ? ` · ${state.cancelTarget.title}` : ''}</strong>}
                confirming={ctrl.isCancelling}
                onClose={ctrl.closeCancel}
                onConfirm={ctrl.confirmCancel}
            />
        </React.Fragment>
    );
}
